// eslint-disable-next-line filenames/match-exported
import { MarketDivider, MarketEmptyState } from '@market/react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { selectIdentifierCollection, selectPrimaryId } from 'store/buyerSlice';
import { IdentifierType } from 'routes/profile/models/Identifier';
import {
  useRetrieveCardPreferencesQuery,
  useRetrieveGlobalEmailPreferencesQuery,
  useRetrieveMerchantEmailPreferencesQuery,
} from 'store/query/api-extensions/receiptAndMarketingPreferences';
import { useListCardsQuery } from 'store/query';
import {
  GlobalEmailPreference,
  Value,
} from 'routes/profile/models/EmailPreference';
import ModuleLoading from '../common/loading/ModuleLoading';
import ModuleLoadFailed from '../common/errors/ModuleLoadFailed';
import BuyerCard from 'routes/profile/models/BuyerCard';
import ReceiptPreferenceToggles from './ReceiptPreferenceToggles';
import ReceiptCardTabs from './ReceiptCardTabs';
import CardGroupsSection from './CardGroupsSection';
import { useRetrieveReceiptSettingsQuery } from 'store/query/api-extensions/receiptSettings';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import Page from '../layout/Page';
import useCdpPageTracking from 'utils/custom-react-hooks/useCdpPageTracking';
import { viewReceiptSettingsProperties } from 'services/tracking/cdp/events/receiptSettings';
import { useTranslation } from 'react-i18next';
import { selectBoolFlag } from 'store/featureSlice';
import { BoolFlag } from 'routes/profile/models/Flags';
import useIsBlockEmployee from 'routes/profile/hooks/useIsBlockEmployee';

function isCardGloballyOptedIn(
  globalEmailPreferences: GlobalEmailPreference | undefined,
  card: BuyerCard,
  linkedEmailId: string | undefined
): boolean {
  if (!linkedEmailId || !globalEmailPreferences) {
    return false;
  }

  const relevantCardPref = globalEmailPreferences.cardReceiptPreferences.find(
    (cardPref) => cardPref.cardId === card.preferenceCardId
  );

  // Edge case: There may not be any card prefs for the current card due to a manually registered card (i.e. not added from suggested identifiers). or
  // the email the card was linked to was deleted, severing the link in buyer service.
  if (!relevantCardPref) {
    return false;
  }

  return relevantCardPref.receiptPreference.value === Value.OptIn;
}

// eslint-disable-next-line complexity
const ReceiptSettings: React.FC = () => {
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const { t } = useTranslation();

  const isBlockEmployee = useIsBlockEmployee();
  const showCardGroups =
    useSelector((state: AppState) =>
      selectBoolFlag(state, BoolFlag.showCardGroupReceiptSettings)
    ) || isBlockEmployee;

  const primaryCardId = useSelector((appState: AppState) =>
    selectPrimaryId(appState, IdentifierType.Card)
  );

  const verifiedEmails = useSelector((state: AppState) =>
    selectIdentifierCollection(state, IdentifierType.Email)
  );

  const {
    data: paymentCards,
    isLoading: isPaymentCardsLoading,
    isError: didPaymentCardsLoadingFail,
  } = useListCardsQuery();

  const {
    isLoading: isReceiptSettingsLoading,
    isError: didReceiptSettingsLoadingFail,
  } = useRetrieveReceiptSettingsQuery(undefined, { skip: !showCardGroups });

  const [selectedPaymentCard, setSelectedPaymentCard] = useState<
    BuyerCard | undefined
  >(undefined);
  const hasInitializedSelectedCard = useRef(false);

  useEffect(() => {
    if (
      paymentCards &&
      !isPaymentCardsLoading &&
      !hasInitializedSelectedCard.current
    ) {
      // Fall back to the first card when primaryCardId is unset or stale
      // (e.g. the primary card was deleted/reissued). Without this fallback,
      // find() returns undefined, selectedPaymentCard is never set, and the
      // page spins forever on the legacy path.
      const initialSelectedCard =
        paymentCards.find((card) => card.cardsApiId === primaryCardId) ??
        paymentCards.at(0);
      if (initialSelectedCard?.cardsApiId) {
        setSelectedPaymentCard(initialSelectedCard);
        hasInitializedSelectedCard.current = true;
      }
    }
  }, [paymentCards, primaryCardId, isPaymentCardsLoading]);

  const retrieveCardPreferencesArg =
    !showCardGroups && selectedPaymentCard?.preferenceCardId
      ? selectedPaymentCard.preferenceCardId
      : skipToken;

  const {
    data: cardResponse,
    isLoading: isCardPreferencesLoading,
    isFetching: isCardPreferencesFetching,
    isError: didCardPreferencesLoadingFail,
  } = useRetrieveCardPreferencesQuery(retrieveCardPreferencesArg);

  const cardPreferences = cardResponse?.cardPreferences ?? [];

  const onCardSelected = useCallback(
    (cardsApiId: string) => {
      const selectedCard = paymentCards?.find(
        (card) => card.cardsApiId === cardsApiId
      );
      setSelectedPaymentCard(selectedCard);
    },
    [paymentCards]
  );

  const linkedEmailId = cardPreferences?.find(
    (pref) => pref.identifier.identifierType === IdentifierType.Email
  )?.identifier?.token;

  const linkedPhoneId = cardPreferences?.find(
    (pref) => pref.identifier.identifierType === IdentifierType.Phone
  )?.identifier?.token;

  const retrieveReceiptPreferencesArg =
    linkedEmailId !== undefined &&
    verifiedEmails.some(({ token }) => token === linkedEmailId)
      ? linkedEmailId
      : skipToken;

  const {
    data: globalEmailPreferences,
    isLoading: isGlobalEmailPreferencesLoading,
    isFetching: isGlobalEmailPreferencesFetching,
    isError: didGlobalEmailPreferencesLoadingFail,
  } = useRetrieveGlobalEmailPreferencesQuery(retrieveReceiptPreferencesArg);

  const retrieveMerchantPreferencesArg =
    retrieveReceiptPreferencesArg === skipToken
      ? skipToken
      : {
          cardId: selectedPaymentCard?.preferenceCardId,
          emailId: retrieveReceiptPreferencesArg,
        };

  const {
    data: receiptMerchantEmailPreferences,
    isLoading: isReceiptMerchantEmailPreferencesLoading,
  } = useRetrieveMerchantEmailPreferencesQuery(retrieveMerchantPreferencesArg);

  const isLegacyLoading =
    !showCardGroups &&
    (isCardPreferencesLoading ||
      isGlobalEmailPreferencesLoading ||
      Boolean(paymentCards && paymentCards.length > 0 && !selectedPaymentCard));

  const isLoading =
    !isInitialLoaded &&
    (isPaymentCardsLoading ||
      isLegacyLoading ||
      (showCardGroups && isReceiptSettingsLoading));

  const didLoadingFail =
    didPaymentCardsLoadingFail ||
    (showCardGroups && didReceiptSettingsLoadingFail) ||
    (!showCardGroups &&
      (didCardPreferencesLoadingFail || didGlobalEmailPreferencesLoadingFail));

  useCdpPageTracking({
    didLoadingFail,
    isLoading,
    onLoad: () => setIsInitialLoaded(true),
    properties: viewReceiptSettingsProperties(
      paymentCards?.map((card) => card.cardsApiId) ?? [],
      linkedEmailId,
      linkedPhoneId,
      selectedPaymentCard
        ? isCardGloballyOptedIn(
            globalEmailPreferences,
            selectedPaymentCard,
            linkedEmailId
          )
        : false,
      selectedPaymentCard?.cardsApiId
    ),
  });

  if (isLoading) {
    return <ModuleLoading />;
  }

  if (didLoadingFail) {
    return <ModuleLoadFailed />;
  }

  const renderEmpty = () => {
    return (
      <MarketEmptyState>
        <p slot={'secondary-text'}>
          {t('profile.receiptSettings.emptyCardText')}
        </p>
      </MarketEmptyState>
    );
  };

  const renderLegacy = () => {
    if (!selectedPaymentCard) {
      return null;
    }

    return (
      <div>
        <ReceiptCardTabs
          paymentCards={paymentCards}
          primaryCardId={primaryCardId}
          selectedPaymentCardId={selectedPaymentCard.cardsApiId}
          onCardSelected={onCardSelected}
          cardPreferences={cardPreferences}
          isRetrievePrefsLoading={isCardPreferencesFetching}
        />
        <MarketDivider className="my-8"></MarketDivider>
        <ReceiptPreferenceToggles
          deliveryEmailId={linkedEmailId ?? null}
          isGloballyOptedIn={isCardGloballyOptedIn(
            globalEmailPreferences,
            selectedPaymentCard,
            linkedEmailId
          )}
          isGlobalToggleDisabled={isGlobalEmailPreferencesFetching}
          selectedPaymentCard={selectedPaymentCard}
          merchantPrefs={receiptMerchantEmailPreferences ?? []}
          isMerchantEmailPreferencesLoading={
            isReceiptMerchantEmailPreferencesLoading
          }
        />
      </div>
    );
  };

  const renderContent = () => {
    if (!paymentCards || paymentCards.length === 0) {
      return renderEmpty();
    }
    if (showCardGroups) {
      return <CardGroupsSection />;
    }
    return renderLegacy();
  };

  return (
    <Page>
      <h1 className={'heading-30 mb-4'}>
        {t('profile.receiptSettings.title')}
      </h1>
      <p className={'mb-8'}>{t('profile.receiptSettings.subtitle')}</p>
      {renderContent()}
    </Page>
  );
};

export default ReceiptSettings;
